import { SCORE_BADGE_ID } from "testIds";
import React from "react";
import styled, { css } from "styled-components";
import { Award, Gem } from "lucide-react";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";

interface NewLookScoreBadgeProps {
  score?: number;
  isTopTrader?: boolean;
  onClick?: () => void;
  setIsScoreHover?: (isScoreHover?: boolean) => void;
  size?: "small" | "default";
}

/**
 * The channel score as a quiet control: the figure in ink on a hairline
 * outline, with a brand-blue mark that says it opens the breakdown. Top
 * traders get an award mark.
 */
const NewLookScoreBadge = ({
  score,
  isTopTrader,
  onClick,
  setIsScoreHover,
  size = "default",
}: NewLookScoreBadgeProps) => {
  if (!score) return null;
  const Icon = isTopTrader ? Award : Gem;
  const isSmall = size === "small";

  return (
    <Badge
      data-testid={SCORE_BADGE_ID}
      type="button"
      $clickable={!!onClick}
      $small={isSmall}
      onClick={(event) => {
        event.stopPropagation();
        onClick?.();
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") event.stopPropagation();
      }}
      onMouseEnter={() => setIsScoreHover?.(true)}
      onMouseLeave={() => setIsScoreHover?.(false)}
    >
      <Icon size={isSmall ? 12 : 14} strokeWidth={1.75} aria-hidden={true} />
      {Math.round(score * 100)}
    </Badge>
  );
};

export default NewLookScoreBadge;

const Badge = styled.button<{ $clickable: boolean; $small: boolean }>`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: ${(p) => (p.$small ? 22 : 26)}px;
  padding: 0 ${(p) => (p.$small ? 6 : 8)}px;
  border: 1px solid ${(p) => getNewLookTokens(p.theme).hairlineStrong};
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  font-family: inherit;
  ${newLookType.figure};
  font-size: ${(p) => (p.$small ? 12 : 13)}px;
  font-weight: 500;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
  transition: border-color 120ms, background-color 120ms, color 120ms;
  svg {
    color: ${(p) => getNewLookTokens(p.theme).accent};
  }
  ${(p) =>
    p.$clickable &&
    css`
      &:hover {
        border-color: ${getNewLookTokens(p.theme).accent};
        background: ${getNewLookTokens(p.theme).rowHover};
        color: ${getNewLookTokens(p.theme).accent};
      }
    `}
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 1px;
  }
`;

interface ScoreRingProps {
  score?: number;
  size?: number;
  onClick?: () => void;
  ariaLabel?: string;
  /** Text inside the ring instead of the score (e.g. "48" for 48 / 50). */
  label?: string;
  /** Renders a plain element (inside another control, e.g. a summary). */
  decorative?: boolean;
  /** The arc's colour instead of the brand blue (e.g. a score category's). */
  color?: string;
}

/**
 * The channel score as the row's lead: a thin ring filled to the score with
 * the figure inside. Clicking opens the score breakdown.
 */
export const ScoreRing = ({
  score,
  size = 36,
  onClick,
  ariaLabel,
  label,
  decorative,
  color,
}: ScoreRingProps) => {
  if (!score) return null;
  const value = Math.max(0, Math.min(100, Math.round(score * 100)));
  const stroke = size >= 64 ? 6 : size >= 48 ? 4 : 3;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <Ring
      as={decorative ? "span" : "button"}
      data-testid={decorative ? undefined : SCORE_BADGE_ID}
      type={decorative ? undefined : "button"}
      aria-hidden={decorative || undefined}
      aria-label={decorative ? undefined : ariaLabel ?? String(value)}
      $size={size}
      $clickable={!!onClick}
      onClick={(event) => {
        event.stopPropagation();
        onClick?.();
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") event.stopPropagation();
      }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          className="track"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
        />
        <circle
          className="value"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${(circumference * value) / 100} ${circumference}`}
          style={color ? { stroke: color } : undefined}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <span>{label ?? value}</span>
    </Ring>
  );
};

const Ring = styled.button<{ $size: number; $clickable: boolean }>`
  position: relative;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  font-family: inherit;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
  svg {
    position: absolute;
    inset: 0;
    /* Let the large ring's glow follow the arc instead of clipping to a box. */
    overflow: visible;
    filter: ${(p) =>
      p.$size >= 64
        ? `drop-shadow(0 2px 6px ${p.theme.colors.primaryBlue}40)`
        : "none"};
  }
  .track {
    stroke: ${(p) => getNewLookTokens(p.theme).hairline};
  }
  .value {
    stroke: ${(p) => getNewLookTokens(p.theme).accent};
    transition: stroke 120ms;
  }
  span {
    position: relative;
    ${newLookType.figure};
    font-size: ${(p) => (p.$size >= 64 ? 28 : p.$size >= 48 ? 16 : 13)}px;
    font-weight: 500;
    color: ${(p) => getNewLookTokens(p.theme).ink};
  }
  ${(p) =>
    p.$clickable &&
    css`
      &:hover span {
        color: ${getNewLookTokens(p.theme).accent};
      }
    `}
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 2px;
  }
`;
